import React, { useState } from 'react'
import { Box, IconButton, Tooltip, Typography, Grid, Divider } from '@mui/material'

import { Button } from '@/@core/components/mui/Button'
import { FilterButton } from '../../common'
import { TableLimit } from '@/@core/components/mui/TableLimit'
import CompanySelection from '../../selection/CompanySelection'
import UserStatusSelection from '../../selection/UserStatusSelection'
import { useRouteQuery, useTranslator, useNotification } from '@/hooks'
import { SearchInput } from '../../common/SearchInput'
import InputDateRangerField from '../../common/input/InputDateRangerField'
import { useExportEmployeeMutation } from '@/@data'

interface EmployeeTableFilterProps {
  onSearch?: (value: string) => void
  onAddEmployee?: () => void
  onOpenLogs?: () => void
  rowSelection?: Record<string, boolean>
}

const EmployeeTableFilter: React.FC<EmployeeTableFilterProps> = ({ onAddEmployee, onOpenLogs, rowSelection = {} }) => {
  const { __ } = useTranslator()
  const { updateQuery, searchQuery } = useRouteQuery()
  const [filter, setFilter] = useState(true)
  const exportEmployeeMutation = useExportEmployeeMutation()
  const n = useNotification()

  return (
    <Box
      sx={{
        display: 'flex',
        padding: '20px',
        gap: 4,
        flexDirection: 'column'
      }}
    >
      {filter && (
        <>
          <Box className='mb-3'>
            <Typography variant='h6' className='mb-3'>
              {__('Filters')}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={4}>
                <CompanySelection
                  value={searchQuery.get('company_id') || ''}
                  onChange={company_id => {
                    updateQuery({ company_id, page: 1 })
                  }}
                  onClear={() => {
                    updateQuery({ company_id: undefined })
                  }}
                />
              </Grid>
              <Grid size={4}>
                <InputDateRangerField
                  value={
                    searchQuery.get('created_from')
                      ? [searchQuery.get('created_from') as string, searchQuery.get('created_to') as string]
                      : []
                  }
                  onChange={created_at => {
                    if (created_at) {
                      updateQuery({ created_from: created_at[0], created_to: created_at[1], page: 1 })
                    } else {
                      updateQuery({ created_from: undefined, created_to: undefined })
                    }
                  }}
                />
              </Grid>
              <Grid size={4}>
                <UserStatusSelection
                  value={searchQuery.get('status') || ''}
                  onSelect={status => {
                    updateQuery({ status, page: 1 })
                  }}
                />
              </Grid>
            </Grid>
          </Box>
          <Divider />
        </>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <TableLimit />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SearchInput
            placeholder={__('Search User')}
            onSearch={value => {
              updateQuery({ keywords: value })
            }}
            initialValue={searchQuery.get('keywords') || ''}
          />

          <FilterButton filter={filter} onFilter={() => setFilter(!filter)} />

          <Button
            variant='tonal'
            color='primary'
            onClick={() => {
              const selectedUserIds = Object.keys(rowSelection).map(Number)
              const hasSelection = selectedUserIds.length > 0
              
              // Check if there are any filters applied
              const companyId = searchQuery.get('company_id')
              // const status = searchQuery.get('status')
              // const createdFrom = searchQuery.get('created_from')
              // const createdTo = searchQuery.get('created_to')
              // const keywords = searchQuery.get('keywords')
              
              const hasFilters = !!companyId
              
              let params: { userIds?: number[]; filterParams?: Record<string, string | undefined> } | undefined
              
              if (hasSelection) {
                // If user has selected specific rows, use those IDs
                params = { userIds: selectedUserIds }
              } else if (hasFilters) {
                // If no selection but has filters, use filter params
                params = {
                  filterParams: {
                    company_id: companyId || undefined,
                    // status: status || undefined,
                    // created_from: createdFrom || undefined,
                    // created_to: createdTo || undefined,
                    // keywords: keywords || undefined
                  }
                }
              } else {
                // No selection and no filters - export all (no params)
                params = undefined
              }

              exportEmployeeMutation.mutate(params, {
                onError: () => {
                  n.error(__('Failed to export employee data'))
                }
              })
            }}
            disabled={exportEmployeeMutation.isPending}
            startIcon={<i className='tabler-upload' />}
            sx={{ height: 40 }}
          >
            {exportEmployeeMutation.isPending ? __('Exporting...') : __('Export Employees')}
          </Button>

          <Tooltip title={__('Add Employee')}>
            <Button
              variant='contained'
              size='small'
              onClick={onAddEmployee}
              startIcon={<i className='tabler-plus' />}
              endIcon={<i className='tabler-chevron-down' />}
              sx={{ height: 40 }}
            >
              {__('Add Employee')}
            </Button>
          </Tooltip>

          <Tooltip title='History'>
            <IconButton
              size='small'
              // onClick={() => {
              //   navigate(`/cms/users/employee/activity-log`)
              // }}
              onClick={onOpenLogs}
              sx={{
                width: 40,
                height: 40,
                backgroundColor: '#2E263D14',
                '&:hover': {
                  backgroundColor: '#e0e0e0'
                }
              }}
            >
              <i className='tabler-history' style={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  )
}

export default EmployeeTableFilter
